import styles from './styles.module.css';
import { useLocation, useNavigate } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import Header from '../../../components/header/Header';
import { useTranslation } from 'react-i18next';
import { Form, Input } from 'antd';
import { useFujiAuth } from '../../../auth/useFujiAuth';
import ButtonNetwork from '../../../utils/network-connect/ButtonNetwork';
import { showNotificationDialog } from '../../../utils';
import CenterLoading from '../../../components/CenterLoading/CenterLoading';
import ConfirmCodeDescription from '../../../components/confirm-code-desscription/ConfirmCodeDescription';
import isOnline from '../../../utils/network-connect/NetworkConnect';

/**
 * 図103-2 パスワード再設定新パスワード入力画面
 */
const ResetPasswordSubmitScreen = () => {
    const { t } = useTranslation();
    const [seconds, setSeconds] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [email, setEmail] = useState('');
    const { forgotPasswordSubmit, forgotPassword } = useFujiAuth();
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        console.log(location.state);
        if (!location.state?.email) {
            navigate('/auth');
        } else {
            setEmail(location.state?.email);
        }
    }, [location, navigate]);

    /**
     *  パスワード再設定の実行
     *
     * @param {Object} params
     * @returns {void}
     */
    const onFinish = ({ code, password }: { code: string; password: string }) => {
        setIsLoading(true);
        forgotPasswordSubmit(
            email,
            code,
            password,
            ({ isSuccessed, error }: { isSuccessed: boolean; error: { code: string; message: string[] } }) => {
                setIsLoading(false);
                if (isSuccessed) {
                    showNotificationDialog(
                        'success',
                        'change-password-success',
                        t('notification.title'),
                        t('notification.change-password-success'),
                    );
                    navigate('/auth');
                } else if (error.code === 'CodeMismatchException' || error.code === 'UserNotFoundException') {
                    // MSG.12-E4 確認コードが正しくない
                    showNotificationDialog(
                        'error',
                        'change-password-error',
                        t('notification.title'),
                        t('error-message.forgot-password.incorrect-code'),
                    );
                } else if (error.code === 'ExpiredCodeException') {
                    // MSG.12-E5確認コード有効期限切れ
                    showNotificationDialog(
                        'error',
                        'change-password-error',
                        t('notification.title'),
                        t('error-message.forgot-password.expired-code'),
                    );
                } else if (error.message.includes('Network error')) {
                    showNotificationDialog('error', '', t('notification.title'), t('error-message.server-error'));
                } else {
                    // MSG.12-E6 ユーザ登録に失敗（その他のエラー）
                    showNotificationDialog(
                        'error',
                        'change-password-error',
                        t('notification.title'),
                        t('error-message.forgot-password.failed'),
                    );
                }
            },
        );
    };

    const onFinishFailed = () => {};

    /**
     * パスワード再設定の確認コードを送信の処理
     *
     * @returns {void}
     */
    const resendConfirmCode = () => {
        if (isOnline()) {
            setIsLoading(true);
            console.log(email);
            forgotPassword(
                email,
                ({ isSuccessed, error }: { isSuccessed: boolean; error: { code: string; message: string[] } }) => {
                    setIsLoading(false);
                    if (isSuccessed || error.code === 'UserNotFoundException') {
                        showNotificationDialog(
                            'success',
                            'resend-confirm-code',
                            t('notification.title'),
                            t('notification.resend-confirm-code'),
                        );
                        if (seconds === 0) {
                            setSeconds(60);
                            const timeInterval = setInterval(() => {
                                setSeconds((second) => {
                                    const number = second - 1;
                                    if (number === 0) {
                                        clearInterval(timeInterval);
                                    }
                                    return number;
                                });
                            }, 1000);
                        }
                    } else if (error.code === 'LimitExceededException') {
                        // MSG.12-E2 確認コードのメールの送信上限に達している。
                        showNotificationDialog(
                            'error',
                            'resend-confirm-code',
                            t('notification.title'),
                            t('error-message.forgot-password.send-code-limit'),
                        );
                    } else {
                        // MSG.12-E3 確認コードのメールの送信に失敗（その他のエラー）
                        showNotificationDialog(
                            'error',
                            'resend-confirm-code',
                            t('notification.title'),
                            t('error-message.forgot-password.send-code-failed'),
                        );
                    }
                },
            );
        } else {
            showNotificationDialog('error', '', t('notification.title'), t('error-message.server-error'));
        }
    };
    return (
        <React.Fragment>
            {isLoading && <CenterLoading overFooter={true} />}
            <Header title={t('header.forgot-password')} />
            <div className={`content-footer-container scrollable ${styles.forgotSubmitcontainer}`}>
                <div className={styles.content}>
                    <ConfirmCodeDescription email={email} />
                    <button disabled={seconds > 0 && true} className={styles.btnResendCode} onClick={resendConfirmCode}>
                        {t('confirm-code.resend-code')} {seconds > 0 && <span>({seconds})</span>}
                    </button>
                    {/* </div> */}
                    <Form
                        className={styles.submitForm}
                        initialValues={{
                            remember: true,
                        }}
                        onFinish={onFinish}
                        onFinishFailed={onFinishFailed}
                    >
                        <Form.Item
                            name="code"
                            rules={[
                                {
                                    required: true,
                                    message: t('validate.correct-code') ?? '',
                                },
                            ]}
                        >
                            <Input
                                className={styles.inputCustom}
                                placeholder={t('form.confirm-code') ?? ''}
                                size="large"
                                maxLength={6}
                                allowClear
                            />
                        </Form.Item>
                        <Form.Item
                            name="password"
                            rules={[
                                {
                                    required: true,
                                    message: t('validate.password') ?? '',
                                },
                                {
                                    min: 8,
                                    message: t('validate.password-8ormore') ?? '',
                                },
                                {
                                    pattern:
                                        // eslint-disable-next-line no-useless-escape
                                        /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])[a-zA-Z0-9=+^$*.\[\]{}()?@#%&/\\,><':;|_~"!`-]/,
                                    message: t('validate.capital-number') ?? '',
                                },
                            ]}
                        >
                            <Input.Password
                                className={styles.inputCustom}
                                placeholder={t('form.new-password') ?? ''}
                                size="large"
                                type="password"
                                autoComplete="password"
                                allowClear
                            />
                        </Form.Item>

                        <Form.Item
                            name="confirm"
                            dependencies={['password']}
                            rules={[
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (getFieldValue('password') === value) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error(t('validate.password-not-match') ?? ''));
                                    },
                                }),
                            ]}
                        >
                            <Input.Password
                                className={styles.inputCustom}
                                placeholder={t('form.new-password-confirm') ?? ''}
                                size="large"
                                autoComplete="password"
                                allowClear
                            />
                        </Form.Item>
                        <p style={{ fontSize: 12.5, marginBottom: 20 }}>{t('password.requirement')}</p>
                        <Form.Item>
                            <div className={styles.btnSubmit}>
                                <ButtonNetwork title={t('button.set-up')} />
                            </div>
                        </Form.Item>
                    </Form>
                </div>
            </div>
        </React.Fragment>
    );
};

export default ResetPasswordSubmitScreen;
